import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { Search, LayoutDashboard, Shield, Menu, X, ChevronDown, PlusCircle, Tag, ClipboardList, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/search?city=${encodeURIComponent(q)}` : '/search');
    setSearchQuery('');
    setMenuOpen(false);
  };

  const handleListProperty = () => {
    setMenuOpen(false);
    if (!user) {
      navigate('/login');
      return;
    }
    if (['owner', 'broker', 'admin'].includes(user.role)) {
      navigate('/add-property');
    } else {
      toast.info('Please switch to Owner or Broker role in your dashboard to list properties.');
      navigate('/dashboard');
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
            <img src="/logo.png" alt="Rent Mitra" className="h-12 w-auto object-contain" />
          </Link>

          {/* Desktop Search Bar */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex flex-1 max-w-sm items-center bg-slate-50 border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all"
            data-testid="navbar-search-form"
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search city, area, pincode…"
              className="flex-1 px-4 py-2.5 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-400 min-w-0"
              data-testid="navbar-search-input"
            />
            <button
              type="submit"
              className="px-3 py-2.5 text-slate-400 hover:text-blue-600 transition-colors flex-shrink-0"
              data-testid="navbar-search-button"
            >
              <Search className="w-4 h-4" />
            </button>
          </form>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1 flex-shrink-0">
            <Link
              to="/blog"
              className="flex items-center gap-1.5 px-3 py-2 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors"
              data-testid="nav-blog"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </Link>

            <Link
              to="/plans"
              className="flex items-center gap-1.5 px-3 py-2 text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors"
              data-testid="nav-pricing"
            >
              <Tag className="w-4 h-4" />
              Pricing
            </Link>

            <Link
              to="/post-requirement"
              className="flex items-center gap-1.5 px-3 py-2 text-slate-600 hover:text-green-700 hover:bg-green-50 rounded-lg text-sm font-medium transition-colors"
              data-testid="nav-post-requirement"
            >
              <ClipboardList className="w-4 h-4" />
              Post Requirement
            </Link>

            <button
              onClick={handleListProperty}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 ml-1"
              data-testid="nav-list-property"
            >
              <PlusCircle className="w-4 h-4" />
              List Property
            </button>
          </div>

          {/* Auth (Desktop) */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {user && <NotificationBell />}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl hover:bg-slate-50 border border-slate-200 transition-colors"
                  data-testid="user-menu-button"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 bg-blue-700 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="text-sm font-medium text-slate-900 max-w-[90px] truncate">{user.name?.split(' ')[0]}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50" data-testid="user-dropdown">
                    <div className="px-4 py-2 border-b border-slate-100 mb-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email || user.phone}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full capitalize font-medium">{user.role}</span>
                    </div>
                    <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setUserMenuOpen(false)} data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4" />Dashboard
                    </Link>
                    {user.role === 'admin' && (
                      <Link to="/admin" className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setUserMenuOpen(false)} data-testid="nav-admin">
                        <Shield className="w-4 h-4" />Admin Panel
                      </Link>
                    )}
                    <hr className="my-1.5 border-slate-100" />
                    <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors" data-testid="logout-button">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-testid="login-button">
                Login
              </Link>
            )}
          </div>

          {/* Mobile: List Property + Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={handleListProperty}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold transition-colors"
              data-testid="mobile-list-property-btn"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              List
            </button>
            <button className="p-2 rounded-lg hover:bg-slate-100" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 space-y-1">
          {/* Mobile Search */}
          <form onSubmit={handleSearch} className="flex items-center bg-slate-50 border border-slate-200 rounded-xl overflow-hidden mb-2">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search city, area, pincode…"
              className="flex-1 px-4 py-2.5 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-400"
              data-testid="mobile-search-input"
            />
            <button type="submit" className="px-3 text-slate-400">
              <Search className="w-4 h-4" />
            </button>
          </form>

          <Link to="/blog" className="flex items-center gap-2 py-2.5 px-3 text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg text-sm" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-blog">
            <BookOpen className="w-4 h-4" />Blog
          </Link>
          <Link to="/plans" className="flex items-center gap-2 py-2.5 px-3 text-slate-700 hover:bg-slate-50 rounded-lg text-sm" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-pricing">
            <Tag className="w-4 h-4" />Pricing
          </Link>
          <Link to="/post-requirement" className="flex items-center gap-2 py-2.5 px-3 text-slate-700 hover:bg-green-50 hover:text-green-700 rounded-lg text-sm" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-post-requirement">
            <ClipboardList className="w-4 h-4" />Post Requirement
          </Link>
          <button onClick={handleListProperty} className="w-full text-left flex items-center gap-2 py-2.5 px-3 text-blue-700 hover:bg-blue-50 rounded-lg text-sm font-medium" data-testid="mobile-nav-list-property">
            <PlusCircle className="w-4 h-4" />List Property
          </button>

          {user ? (
            <>
              <Link to="/dashboard" className="flex items-center gap-2 py-2.5 px-3 text-slate-700 hover:bg-slate-50 rounded-lg text-sm" onClick={() => setMenuOpen(false)}>
                <LayoutDashboard className="w-4 h-4" />Dashboard
              </Link>
              {user.role === 'admin' && (
                <Link to="/admin" className="flex items-center gap-2 py-2.5 px-3 text-slate-700 hover:bg-slate-50 rounded-lg text-sm" onClick={() => setMenuOpen(false)}>
                  <Shield className="w-4 h-4" />Admin Panel
                </Link>
              )}
              <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 py-2.5 px-3 text-red-500 hover:bg-red-50 rounded-lg text-sm mt-1">
                Logout
              </button>
            </>
          ) : (
            <Link to="/login" className="block py-2.5 px-3 text-blue-700 font-semibold text-sm" onClick={() => setMenuOpen(false)}>
              Login / Register
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}
